import React, { useEffect, useState } from 'react';
import Shell from '../../components/Shell';
import api from '../../services/api';

type Status = {
  contentRoot: string;
  fundCount: number;
  funds: {
    slug: string;
    name: string;
    published: boolean;
    status: string;
    documentCount: number;
    missingDocs: string[];
  }[];
  restructuring?: {
    path: string;
    loaded: boolean;
    published: boolean;
    title: string | null;
    companyCount: number;
    chartCount: number;
  };
};

export default function AdminContentPage() {
  const [status, setStatus] = useState<Status | null>(null);
  const [msg, setMsg] = useState('');
  const [busySlug, setBusySlug] = useState<string | null>(null);

  async function load() {
    const { data } = await api.get('/admin/content-status');
    setStatus(data);
  }

  useEffect(() => {
    load().catch(() => setMsg('Failed to load content status'));
  }, []);

  async function reload() {
    const { data } = await api.post('/admin/content-reload');
    setStatus(data);
    setMsg('Reloaded from disk.');
  }

  async function setPublished(slug: string, published: boolean) {
    setBusySlug(slug);
    setMsg('');
    try {
      const { data } = await api.patch(`/admin/funds/${slug}/published`, {
        published,
      });
      setStatus(data);
      setMsg(
        published
          ? `${slug} is now live on the portal.`
          : `${slug} is hidden from the portal.`,
      );
    } catch (err: any) {
      setMsg(err?.response?.data?.message || 'Failed to update visibility');
    } finally {
      setBusySlug(null);
    }
  }

  return (
    <Shell title="Admin · Content">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Content status</h1>
          <p className="mt-2 max-w-xl text-sm text-ink-700">
            Fund narrative is edited as markdown under{' '}
            <code className="text-xs">content/</code>. Use Live / Hidden here so
            unfinished fund docs never appear on the investor portal. Visibility
            changes are saved in the database (and to disk when writable).
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          className="bg-navy-900 px-4 py-2 text-sm font-semibold text-mist-50 hover:bg-navy-700"
        >
          Reload from disk
        </button>
      </div>
      {msg && <p className="mt-3 text-sm text-ink-700">{msg}</p>}
      {status && (
        <div className="mt-8 space-y-10">
          <div>
            <p className="text-xs text-ink-700">
              Root: {status.contentRoot} · {status.fundCount} funds
            </p>
            <ul className="mt-4 divide-y divide-mist-200 border-y border-mist-200">
              {status.funds.map((f) => (
                <li
                  key={f.slug}
                  className="flex flex-wrap items-center justify-between gap-3 py-4"
                >
                  <div>
                    <p className="font-medium">
                      {f.name}{' '}
                      <span className="text-sm font-normal text-ink-700">
                        ({f.slug})
                      </span>
                    </p>
                    <p className="text-sm text-ink-700">
                      {f.published ? 'live' : 'hidden'} · {f.status} ·{' '}
                      {f.documentCount} docs
                    </p>
                    {f.missingDocs.length > 0 && (
                      <p className="mt-1 text-sm text-red-700">
                        Missing: {f.missingDocs.join(', ')}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={busySlug === f.slug}
                    onClick={() => setPublished(f.slug, !f.published)}
                    className="text-sm underline disabled:opacity-50"
                  >
                    {f.published ? 'Set hidden' : 'Set live'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {status.restructuring && (
            <div>
              <h2 className="font-display text-xl">Restructuring page</h2>
              <p className="mt-2 text-sm text-ink-700">
                {status.restructuring.path}
              </p>
              <p className="mt-1 text-sm text-ink-700">
                {status.restructuring.loaded ? 'loaded' : 'missing'} ·{' '}
                {status.restructuring.published ? 'published' : 'hidden'} ·{' '}
                {status.restructuring.companyCount} companies ·{' '}
                {status.restructuring.chartCount} charts
                {status.restructuring.title
                  ? ` · ${status.restructuring.title}`
                  : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}
